import React from 'react';

import { Box } from '@tanium/react-box';
import styled from '@tanium/react-emotion-9';
import { ChevronRightIcon, Icon } from '@tanium/react-graphics';
import { Stack, StackChild } from '@tanium/react-stack';
import { useTheme } from '@tanium/react-theme-context';
import { CollapsedCollapsibleProps, UseCollapsedReturnValue } from '@tanium/react-use-collapsed';
import { css } from '@tanium/style-system';
import { ThemeFontWeights } from '@tanium/theme-data';

import { IconFill, Toggle, ToggleHoverBackground } from './themeDefinitions';

export interface Props
  extends Pick<CollapsedCollapsibleProps, 'isCollapsible'>,
    Pick<UseCollapsedReturnValue, 'collapseControlProps' | 'isCollapsed'> {
  /**
   * Optional additional content to display next to the title.
   */
  additionalTitleContent?: React.ReactNode;
  /**
   * Optional icon to display next to the title.
   */
  icon?: Icon;
  /**
   * The title to display for the section.
   */
  title: React.ReactNode;
}

const Disclosure = styled(ChevronRightIcon, { shouldForwardProp: (p) => p !== 'isCollapsed' })<{
  isCollapsed: boolean;
}>(
  css({
    display: 'block',
    color: Toggle,
    width: 4,
    height: 4,
    padding: 1,
    mx: -1,
    borderRadius: '50%',
    bg: 'transparent',
    transition: `background .3s, transform .3s`,
    ':hover': {
      bg: ToggleHoverBackground,
    },
  }),
  ({ isCollapsed }) => ({
    transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
  }),
);

const HeaderStack = styled(Stack)(
  css({
    minHeight: 4,
  }),
);

const displayBlockStyle = { display: 'block' };

/**
 * Header for PageSection
 */
const PageSectionHeader = ({
  additionalTitleContent,
  collapseControlProps,
  icon: IconComponent,
  isCollapsed,
  isCollapsible,
  title,
}: Props) => {
  const theme = useTheme();
  const iconColor = IconFill(theme);

  return (
    <HeaderStack itemSpacing={2} justifyContent="space-between">
      <HeaderStack itemSpacing={1} alignItems="center">
        {IconComponent && (
          <StackChild shrink={0}>
            <IconComponent style={displayBlockStyle} height={16} width={16} color={iconColor} />
          </StackChild>
        )}
        {/* 
            "21px" `lineHeight` here results in icon being vertically centered based on text 
            "cap height" (descender is disregarded). 
          */}
        <Box fontWeight={ThemeFontWeights.Bold} fontSize="18px" lineHeight="21px">
          {title}
        </Box>
      </HeaderStack>
      {additionalTitleContent && (
        <StackChild grow={1} display="flex">
          {additionalTitleContent}
        </StackChild>
      )}
      {isCollapsible && (
        <StackChild shrink={0}>
          <Disclosure isCollapsed={isCollapsed} {...collapseControlProps} />
        </StackChild>
      )}
    </HeaderStack>
  );
};

export default PageSectionHeader;
